import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { addError } from '@shared/utils/logger';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';
import { useCallback } from 'react';

export type WebsiteInterceptExternalHotelLinksPayload = ReadonlyArray<HotelFilter>;
export interface HotelFilter {
  hostname: ReadonlyArray<string>;
  matchingLogic: ReadonlyArray<
    Readonly<{
      pathname?: string;
      search?: string;
      result: boolean;
    }>
  >;
}

// Patterns come from the feature flag payload and are re-tried on every link check.
const reportedInvalidPatterns = new Set<string>();

const tryCreateRegex = (regexString?: string): RegExp | null => {
  try {
    if (regexString) {
      return new RegExp(regexString, 'i');
    }
  } catch (e) {
    if (regexString && !reportedInvalidPatterns.has(regexString)) {
      reportedInvalidPatterns.add(regexString);
      addError(e instanceof Error ? e : new Error(String(e)), { context: 'regexParseError', pattern: regexString });
    }
  }
  return null;
};

// A scheme with no dot, so `example.com:8080` still reads as a host with a port.
const SCHEME_PREFIX = /^[a-z][a-z\d+-]*:/i;
const WEB_SCHEME_PREFIX = /^https?:/i;

/**
 * Parses a couple-entered link as an external web URL, or returns null. Same-page links (`#`, `?`, `/path`),
 * non-web schemes (`mailto:`, `tel:`) and text that isn't a URL are expected input, not errors.
 */
export const parseUrl = (url: string): URL | null => {
  const trimmedUrl = url.trim();
  // A single leading slash is a same-site path; `https:///path` would otherwise parse with `path` as the host.
  if (!trimmedUrl || /^\/(?!\/)/.test(trimmedUrl)) {
    return null;
  }
  if (SCHEME_PREFIX.test(trimmedUrl) && !WEB_SCHEME_PREFIX.test(trimmedUrl)) {
    return null;
  }
  try {
    return new URL(ensureHttpsPrefix(trimmedUrl));
  } catch {
    return null;
  }
};

export const shouldUseHotelRedirect = (url: string, filters: ReadonlyArray<HotelFilter>): boolean => {
  const parsedUrl = parseUrl(url);

  // If the URL is invalid do not show the hotel redirect
  if (!parsedUrl) {
    return false;
  }

  // Check if any top-level domain filter matches
  const matchingFilter = filters.find(filter =>
    filter.hostname.some(regexStr => {
      const regex = tryCreateRegex(regexStr);
      return !!regex?.test(parsedUrl.hostname || '');
    }),
  );

  // If no matching top-level domain, return false (don't use hotel redirect)
  if (!matchingFilter) {
    return false;
  }

  const matchingLogicResult = matchingFilter.matchingLogic.find(item => {
    const searchRegex = tryCreateRegex(item.search);
    const matches = [];
    if (searchRegex && parsedUrl.search) matches.push(searchRegex.test(parsedUrl.search));

    const pathnameRegex = tryCreateRegex(item.pathname);
    if (pathnameRegex && parsedUrl.pathname) matches.push(pathnameRegex.test(parsedUrl.pathname));

    return matches.length && matches.every(match => match === true);
  })?.result;

  return !!matchingLogicResult;
};

export const useWebsiteInterceptExternalHotelLinks = () => {
  const [isInterceptEnabled, isInterceptKeyQueryLoading] = useIsFeatureEnabled({ consentKey: 'websiteAllowHotelLinkIntercept' });

  const { value, payload } = useFeatureValue('websiteInterceptExternalHotelLinksV2');
  const websiteInterceptExternalHotelLinksPayload = payload as WebsiteInterceptExternalHotelLinksPayload;

  const getShowIntercept = useCallback(
    (link: string) => {
      if (websiteInterceptExternalHotelLinksPayload) {
        return value === 'on' && isInterceptEnabled && shouldUseHotelRedirect(link, websiteInterceptExternalHotelLinksPayload);
      }
      return false;
    },
    [isInterceptEnabled, value, websiteInterceptExternalHotelLinksPayload],
  );

  return { getShowIntercept, isInterceptKeyQueryLoading };
};
